// CreateQRModal.js
import React from "react";
import { Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../context/authContext";
import SubscriptionOverlay from "../../../UI/SubscriptionOverlay";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import { message } from "antd";

const CreateQRModal = ({
  visible,
  onCancel,
  onSubmit,
  name,
  onNameChange,
  selectedFormId,
  onSelectedFormIdChange,
  feedbackForms,
  loading,
  onLogoChange,
}) => {
  const { subscriptionLevel } = useAuth();
  const { language } = useLanguage();
  const text = dashboardContent[language].qr.createmodal;

  if (!visible) return null;

  const handleSVGtoPNGConversion = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const svgText = event.target.result;

          // SVG im DOM erzeugen
          const svg = new Blob([svgText], { type: "image/svg+xml" });
          const url = URL.createObjectURL(svg);

          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            canvas.toBlob((blob) => {
              const pngFile = new File(
                [blob],
                file.name.replace(/\.svg$/, ".png"),
                {
                  type: "image/png",
                  lastModified: new Date().getTime(),
                }
              );
              resolve(pngFile);
            }, "image/png");
          };

          img.onerror = () => {
            reject(new Error("Bild konnte nicht geladen werden"));
          };

          img.src = url;
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () =>
        reject(new Error("Datei konnte nicht gelesen werden"));
      reader.readAsText(file);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-md p-6 rounded shadow-lg">
        <h3 className="text-lg font-semibold mb-4">{text.title}</h3>
        {/* Wichtig: encType="multipart/form-data" hinzufügen */}
        <form
          onSubmit={onSubmit}
          className="space-y-4"
          encType="multipart/form-data"
        >
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              {text.nameLabel}
            </label>
            <input
              id="name"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500"
              value={name}
              onChange={onNameChange}
              placeholder={text.namePlaceholder}
              required
            />
          </div>
          <div>
            <label
              htmlFor="feedbackForm"
              className="block text-sm font-medium text-gray-700"
            >
              {text.formLabel}
            </label>
            <select
              id="feedbackForm"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500"
              value={selectedFormId}
              onChange={onSelectedFormIdChange}
              required
            >
              <option value="">{text.formPlaceholder}</option>
              {feedbackForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700">
              {text.logoLabel}
            </label>

            <Upload
              accept="image/*"
              beforeUpload={async (file) => {
                if (file.type === "image/svg+xml") {
                  try {
                    const pngFile = await handleSVGtoPNGConversion(file);
                    console.log("✅ Konvertiertes PNG:", pngFile);
                    onLogoChange(pngFile);
                    return false;
                  } catch (err) {
                    console.error("❌ SVG-Konvertierung fehlgeschlagen:", err);
                    message.error("SVG konnte nicht konvertiert werden.");
                    return Upload.LIST_IGNORE;
                  }
                } else {
                  console.log("✅ Normales Bild:", file);
                  onLogoChange(file);
                  return false;
                }
              }}
              maxCount={1}
              showUploadList={true}
              disabled={subscriptionLevel < 2}
            >
              {subscriptionLevel >= 2 ? (
                <Button icon={<UploadOutlined />}>{text.logoButton}</Button>
              ) : (
                <div className="w-full">
                  <SubscriptionOverlay
                    onUpgrade={() => (window.location.href = "/pricing")}
                    blur={false}
                  />
                </div>
              )}
            </Upload>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              onClick={onCancel}
            >
              {text.cancel}
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded text-white ${
                loading ? "bg-orange-400" : "bg-orange-500 hover:bg-orange-600"
              }`}
              disabled={loading}
            >
              {loading ? text.creating : text.create}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateQRModal;
